import * as React from 'react';

import { Typography, Typography2 } from '@coursera/cds-core';
import { Box, StyleSheet, css } from '@coursera/coursera-ui';

import type { InformationCardProps } from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCard';
import InformationCard from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCard';
import InformationCardBody from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardBody';
import { TabCaption } from 'bundles/enterprise-legacy-learner-home/components/InformationCard/typography';

import _t from 'i18n!nls/enterprise-admin-skills';

type AboutTargetLevelsInformationCardProps = InformationCardProps;

const styles = StyleSheet.create({
  root: {
    padding: '23px 6px',
  },
  column: {
    flex: '0 1 50%',

    ':nth-child(n) > div:nth-child(n+2)': {
      marginLeft: '6rem',
    },
  },
  list: {
    listStyle: 'none',
    fontSize: '0.875rem',
    paddingLeft: 0,
  },
  listItem: {
    paddingLeft: '1rem',
    textIndent: '-1rem',
    marginBottom: '1rem',
    lineHeight: '1.5rem',
    ':before': {
      content: "'\\2023'",
      marginRight: '0.6875rem',
    },
  },
  paragraph: {
    marginBottom: '10px',
  },
});

const AboutTargetLevelsInformationCard: React.FC<AboutTargetLevelsInformationCardProps> = (props) => (
  <InformationCard {...props} rootClassName={css(styles.root, props.rootClassName).className}>
    <InformationCardBody>
      <TabCaption>{_t('About proficiency levels and targets')}</TabCaption>

      <Box rootClassName={styles.column}>
        <div {...css(styles.column)}>
          <Typography2 variant="bodyPrimary" component="strong">
            {_t('Proficiency levels')}
          </Typography2>
          <ul {...css(styles.list)}>
            <li {...css(styles.listItem)}>
              {_t(
                'A SkillSet defines a target attainment level for each skill within it, learners achieve a SkillSet when they reach the target levels across these skills.'
              )}
            </li>
            <li {...css(styles.listItem)}>
              <Typography2 variant="bodyPrimary" component="strong">
                {_t('Conversant level ')}
              </Typography2>
              {_t('indicates that a learner can speak to the importance of a skill and/or its applicability.')}
            </li>
            <li {...css(styles.listItem)}>
              <Typography2 variant="bodyPrimary" component="strong">
                {_t('Beginner level ')}
              </Typography2>
              {_t(
                'means a learner is on their way to developing application of that skill (beyond just talking about its importance or how it might be used).'
              )}
            </li>
            <li {...css(styles.listItem)}>
              <Typography2 variant="bodyPrimary" component="strong">
                {_t('Intermediate level ')}
              </Typography2>
              {_t(
                'indicates the ability to apply basic concepts on the job. A learner can typically reach the intermediate level by passing the assessments in one or two courses.'
              )}
            </li>
            <li {...css(styles.listItem)}>
              <Typography2 variant="bodyPrimary" component="strong">
                {_t('Advanced level ')}
              </Typography2>
              {_t(
                'indicates mastery of the material and ability to both teach others and identify novel applications of skills. A learner would typically move from intermediate to advanced after passing assessments across several intermediate courses.'
              )}
            </li>
          </ul>
        </div>
        <div {...css(styles.column)}>
          <Typography2 variant="bodyPrimary" component="strong">
            {_t('Proficiency targets')}
          </Typography2>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography {...css(styles.paragraph)}>
            {_t(
              'Proficiency targets are numerical scores to communicate skill development goals and expectations to learners.'
            )}
          </Typography>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography {...css(styles.paragraph)}>
            {_t(
              'Each skill in a SkillSet has a proficiency target that learners must achieve. Once they achieve the proficiency target for all skills in the SkillSet, they are considered as having achieved that SkillSet.'
            )}
          </Typography>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography {...css(styles.paragraph)}>
            {_t(
              'The proficiency targets in a SkillSet are set using Coursera platform data and by subject matter experts.'
            )}
          </Typography>
        </div>
      </Box>
    </InformationCardBody>
  </InformationCard>
);

export default AboutTargetLevelsInformationCard;
